import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { api, formatErr } from "../lib/api";
import { LOGO_MIFORMALIN } from "../lib/operationTypes";
import { Shield, UserPlus, LogIn } from "lucide-react";

export default function Landing() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ username: "", password: "", full_name: "", confirm: "", unit: "" });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.username, form.password);
      toast.success("Selamat datang");
      nav("/dashboard");
    } catch (err) { toast.error(formatErr(err)); }
    finally { setLoading(false); }
  };

  const onRegister = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) return toast.error("Konfirmasi password tidak sama");
    setLoading(true);
    try {
      await api.post("/auth/register", {
        full_name: form.full_name,
        username: form.username,
        password: form.password,
        unit: form.unit,
      });
      toast.success("Pendaftaran berhasil. Menunggu persetujuan Administrator.");
      setMode("login");
    } catch (err) { toast.error(formatErr(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A1628] text-white flex flex-col items-center justify-center p-6 relative overflow-hidden">
      {/* Decorative gold rays */}
      <div className="absolute inset-0 pointer-events-none opacity-30">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120vh] h-[120vh] rounded-full" style={{ background: "radial-gradient(circle, rgba(212,175,55,0.12) 0%, transparent 60%)" }} />
      </div>

      <div className="relative z-10 flex flex-col items-center w-full max-w-md">
        <img src={LOGO_MIFORMALIN} alt="MIFORMALIN" className="w-48 h-48 object-contain drop-shadow-[0_0_40px_rgba(212,175,55,0.25)]" />
        <h1 className="mf-heading text-4xl sm:text-5xl mt-4 tracking-tight">MIFORMALIN</h1>
        <p className="text-[#A0B4C8] text-center mt-3 text-sm tracking-wide font-display italic">
          "Mengawetkan Data, Menguatkan Bukti"
        </p>
        <div className="text-center text-[12px] uppercase tracking-[0.3em] text-[#5B8DB8] mt-1">Manajemen Informasi Forensik dan Medikolegal Terintegrasi</div>

        <div className="w-full mt-8 bg-white/[0.04] border border-[#C9A84C]/30 backdrop-blur-md rounded-2xl p-7">
          <div className="flex gap-2 mb-6">
            <button onClick={() => setMode("login")} data-testid="tab-login" className={`flex-1 h-11 rounded-md text-sm uppercase tracking-[0.18em] transition-all ${mode === "login" ? "bg-gradient-to-r from-[#C9A84C] to-[#D4AF37] text-[#0A1628] font-bold" : "text-[#A0B4C8] hover:text-white"}`}>
              <LogIn size={14} className="inline mr-2"/> Login
            </button>
            <button onClick={() => setMode("register")} data-testid="tab-register" className={`flex-1 h-11 rounded-md text-sm uppercase tracking-[0.18em] transition-all ${mode === "register" ? "bg-gradient-to-r from-[#C9A84C] to-[#D4AF37] text-[#0A1628] font-bold" : "text-[#A0B4C8] hover:text-white"}`}>
              <UserPlus size={14} className="inline mr-2"/> Daftar
            </button>
          </div>

          {mode === "login" ? (
            <form onSubmit={onLogin} className="space-y-4">
              <div>
                <label className="mf-label">Username</label>
                <input value={form.username} onChange={set("username")} className="mf-input" required data-testid="login-username" autoComplete="username"/>
              </div>
              <div>
                <label className="mf-label">Password</label>
                <input type="password" value={form.password} onChange={set("password")} className="mf-input" required data-testid="login-password" autoComplete="current-password"/>
              </div>
              <button type="submit" disabled={loading} className="mf-btn-primary w-full" data-testid="login-submit">
                {loading ? "Memproses..." : "Masuk"}
              </button>
              <div className="text-[10px] text-[#5B8DB8] text-center tracking-widest">
                <Shield size={10} className="inline mr-1"/> Sistem internal · Akses dibatasi
              </div>
            </form>
          ) : (
            <form onSubmit={onRegister} className="space-y-4">
              <div>
                <label className="mf-label">Nama Lengkap</label>
                <input value={form.full_name} onChange={set("full_name")} className="mf-input" required data-testid="register-fullname"/>
              </div>
              <div>
                <label className="mf-label">Username</label>
                <input value={form.username} onChange={set("username")} className="mf-input" required data-testid="register-username"/>
              </div>
              <div>
                <label className="mf-label">Unit / Jabatan</label>
                <input value={form.unit} onChange={set("unit")} className="mf-input" placeholder="Mis. Biddokkes Polda Jabar" data-testid="register-unit"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mf-label">Password</label>
                  <input type="password" value={form.password} onChange={set("password")} className="mf-input" required data-testid="register-password"/>
                </div>
                <div>
                  <label className="mf-label">Konfirmasi</label>
                  <input type="password" value={form.confirm} onChange={set("confirm")} className="mf-input" required data-testid="register-confirm"/>
                </div>
              </div>
              <button type="submit" disabled={loading} className="mf-btn-primary w-full" data-testid="register-submit">
                {loading ? "Memproses..." : "Daftar Akun Baru"}
              </button>
            </form>
          )}
        </div>
      </div>
      <div className="fixed bottom-2 right-3 z-30 text-[8px] text-[#5B8DB8]/50 pointer-events-none select-none">
        Created by Uray A. Juviar @2026 ver 1.2
      </div>
    </div>
  );
}
